/**
 * Physician Orders Overlay Component
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { ScrollArea } from "../ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Stethoscope, Plus } from "lucide-react";
import { toast } from "sonner";

export function PhysicianOrdersOverlay({ patientId, patientName, onSave, onClose }) {
  const [orders, setOrders] = useState([
    { id: "1", order_type: "medication", description: "Morphine Sulfate 15mg PO q4h PRN pain", physician: "Dr. Smith", order_date: "2026-01-20", status: "active" },
    { id: "2", order_type: "treatment", description: "Wound care to sacrum daily with NS and dry dressing", physician: "Dr. Johnson", order_date: "2026-01-18", status: "active" },
    { id: "3", order_type: "dme", description: "Hospital bed with trapeze", physician: "Dr. Smith", order_date: "2026-01-15", status: "completed" },
  ]);
  const [showAdd, setShowAdd] = useState(false);
  const [newOrder, setNewOrder] = useState({
    order_type: "medication",
    order_date: new Date().toISOString().split("T")[0],
    physician: "",
    order_method: "telephone",
    description: "",
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
    frequency: "",
    special_instructions: "",
    verbal_read_back: false,
    signature_obtained: false
  });

  const orderTypes = [
    { value: "medication", label: "Medication Order" },
    { value: "treatment", label: "Treatment Order" },
    { value: "dme", label: "DME Order" },
    { value: "diet", label: "Diet Order" },
    { value: "activity", label: "Activity Order" },
    { value: "lab", label: "Lab Order" },
    { value: "referral", label: "Referral" },
    { value: "dc_med", label: "Discontinue Medication" },
    { value: "dc_treatment", label: "Discontinue Treatment" },
  ];

  const orderMethods = [
    { value: "telephone", label: "Telephone Order (TO)" },
    { value: "verbal", label: "Verbal Order (VO)" },
    { value: "written", label: "Written Order" },
    { value: "electronic", label: "Electronic Order" },
  ];

  const handleAddOrder = () => {
    if (!newOrder.description || !newOrder.physician) {
      toast.error("Please complete order description and physician name");
      return;
    }
    const order = { ...newOrder, id: Date.now().toString(), status: "pending_signature" };
    setOrders(prev => [order, ...prev]);
    setShowAdd(false);
    setNewOrder({
      order_type: "medication",
      order_date: new Date().toISOString().split("T")[0],
      physician: "",
      order_method: "telephone",
      description: "",
      start_date: new Date().toISOString().split("T")[0],
      end_date: "",
      frequency: "",
      special_instructions: "",
      verbal_read_back: false,
      signature_obtained: false
    });
    toast.success("Order added - pending physician signature");
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case "active": return <Badge className="bg-green-500">Active</Badge>;
      case "pending_signature": return <Badge className="bg-amber-500">Pending Signature</Badge>;
      case "completed": return <Badge className="bg-slate-500">Completed</Badge>;
      case "discontinued": return <Badge className="bg-red-500">DC&apos;d</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="space-y-4 p-4" data-testid="physician-orders-overlay">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Stethoscope className="w-5 h-5 text-violet-400" />
          <h3 className="font-semibold text-white">Physician Orders</h3>
          <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)} className="bg-violet-600 hover:bg-violet-700">
          <Plus className="w-4 h-4 mr-1" />New Order
        </Button>
      </div>

      <Tabs defaultValue="active" className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-slate-800">
          <TabsTrigger value="active">Active Orders</TabsTrigger>
          <TabsTrigger value="pending">Pending Signature</TabsTrigger>
          <TabsTrigger value="all">All Orders</TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          <ScrollArea className="h-[400px] pr-2">
            <div className="space-y-2">
              {orders.filter(o => o.status === "active").map(order => (
                <div key={order.id} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="border-slate-600 text-slate-300">{orderTypes.find(t => t.value === order.order_type)?.label}</Badge>
                        {getStatusBadge(order.status)}
                      </div>
                      <p className="font-medium text-sm text-white">{order.description}</p>
                      <p className="text-xs text-slate-400 mt-1">{order.physician} | {order.order_date}</p>
                    </div>
                  </div>
                </div>
              ))}
              {orders.filter(o => o.status === "active").length === 0 && (
                <p className="text-center text-slate-400 py-8">No active orders</p>
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="pending">
          <ScrollArea className="h-[400px] pr-2">
            <div className="space-y-2">
              {orders.filter(o => o.status === "pending_signature").map(order => (
                <div key={order.id} className="p-3 rounded-lg bg-amber-900/20 border border-amber-500/30">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="border-slate-600 text-slate-300">{orderTypes.find(t => t.value === order.order_type)?.label}</Badge>
                        {getStatusBadge(order.status)}
                      </div>
                      <p className="font-medium text-sm text-white">{order.description}</p>
                      <p className="text-xs text-slate-400 mt-1">{order.physician} | {order.order_date}</p>
                    </div>
                    <Button size="sm" variant="outline" className="border-amber-500/50 text-amber-300">Request Signature</Button>
                  </div>
                </div>
              ))}
              {orders.filter(o => o.status === "pending_signature").length === 0 && (
                <p className="text-center text-slate-400 py-8">No pending orders</p>
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="all">
          <ScrollArea className="h-[400px] pr-2">
            <div className="space-y-2">
              {orders.map(order => (
                <div key={order.id} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="border-slate-600 text-slate-300">{orderTypes.find(t => t.value === order.order_type)?.label}</Badge>
                        {getStatusBadge(order.status)}
                      </div>
                      <p className="font-medium text-sm text-white">{order.description}</p>
                      <p className="text-xs text-slate-400 mt-1">{order.physician} | {order.order_date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>

      {/* Add Order Dialog */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAdd(false)}>
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-6 w-[550px] max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h4 className="font-semibold mb-4 flex items-center gap-2 text-white"><Stethoscope className="w-4 h-4" />New Physician Order</h4>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-300">Order Type</Label>
                  <Select value={newOrder.order_type} onValueChange={v => setNewOrder({...newOrder, order_type: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>{orderTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-300">Order Method</Label>
                  <Select value={newOrder.order_method} onValueChange={v => setNewOrder({...newOrder, order_method: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>{orderMethods.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-300">Ordering Physician</Label>
                  <Input value={newOrder.physician} onChange={e => setNewOrder({...newOrder, physician: e.target.value})} placeholder="Dr. Name" className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-300">Order Date</Label>
                  <Input type="date" value={newOrder.order_date} onChange={e => setNewOrder({...newOrder, order_date: e.target.value})} className="bg-slate-800 border-slate-700 text-white" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-300">Order Description</Label>
                <Textarea value={newOrder.description} onChange={e => setNewOrder({...newOrder, description: e.target.value})} placeholder="e.g., Morphine Sulfate 15mg PO q4h PRN pain" rows={3} className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-300">Special Instructions</Label>
                <Input value={newOrder.special_instructions} onChange={e => setNewOrder({...newOrder, special_instructions: e.target.value})} placeholder="Any special instructions..." className="bg-slate-800 border-slate-700 text-white" />
              </div>
              {(newOrder.order_method === "telephone" || newOrder.order_method === "verbal") && (
                <div className="flex items-center justify-between p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg">
                  <div>
                    <Label className="text-slate-200">Verbal Read-Back Completed</Label>
                    <p className="text-xs text-amber-400">Required for TO/VO orders</p>
                  </div>
                  <Switch checked={newOrder.verbal_read_back} onCheckedChange={c => setNewOrder({...newOrder, verbal_read_back: c})} />
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAdd(false)} className="border-slate-600 text-slate-300">Cancel</Button>
                <Button onClick={handleAddOrder} className="bg-violet-600 hover:bg-violet-700"><Plus className="w-4 h-4 mr-1" />Add Order</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Close</Button>
      </div>
    </div>
  );
}

export default PhysicianOrdersOverlay;
